import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { retailerAPI, paymentAPI } from '../../../utils/api';

const RetailerWallet = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [balance, setBalance] = useState(0);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    ifscCode: '',
    accountNumber: '',
    confirmAccountNumber: ''
  });
  const [loading, setLoading] = useState(true);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showConfirmAccountNumber, setShowConfirmAccountNumber] = useState(false);

  useEffect(() => {
    fetchWalletBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const res = await retailerAPI.getWallet();
      if (res.data?.success) {
        setBalance(res.data.data?.balance || user?.wallet_balance || 0);
      }
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    if (parseFloat(withdrawAmount) > balance) {
      showError('Insufficient balance');
      return;
    }

    if (!bankDetails.bankName || !bankDetails.ifscCode || !bankDetails.accountNumber) {
      showError('Please fill in all bank details');
      return;
    }

    if (bankDetails.accountNumber !== bankDetails.confirmAccountNumber) {
      showError('Account numbers do not match');
      return;
    }

    try {
      const res = await paymentAPI.requestWithdrawal({
        amount: parseFloat(withdrawAmount),
        bank_name: bankDetails.bankName,
        ifsc_code: bankDetails.ifscCode,
        account_number: bankDetails.accountNumber
      });
      if (res.data?.success) {
        showSuccess('Withdrawal request submitted successfully. 2% deduction will be applied.');
        setWithdrawAmount('');
        setBankDetails({
          bankName: '',
          ifscCode: '',
          accountNumber: '',
          confirmAccountNumber: ''
        });
        fetchWalletBalance();
      } else {
        showError(res.data?.message || 'Failed to submit withdrawal request');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to submit withdrawal request');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
        <p className="text-gray-400 mt-2">Loading wallet...</p>
      </div>
    );
  }

  const withdrawalFee = withdrawAmount ? (parseFloat(withdrawAmount) * 0.02).toFixed(2) : 0;
  const netAmount = withdrawAmount ? (parseFloat(withdrawAmount) - parseFloat(withdrawalFee)).toFixed(2) : 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gold">My Wallet</h1>

      <div className="bg-gradient-to-r from-gold to-yellow-600 rounded-xl p-6 text-dark">
        <div className="text-center">
          <p className="text-lg opacity-80 mb-2">Available Balance</p>
          <p className="text-4xl font-bold">₹{balance.toFixed(2)}</p>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Withdraw Earnings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-400 mb-2">Amount to Withdraw</label>
            <input
              type="number"
              placeholder="Enter amount"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 mb-2">Bank Name</label>
              <input
                type="text"
                placeholder="e.g. HDFC Bank"
                value={bankDetails.bankName}
                onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
            <div>
              <label className="block text-gray-400 mb-2">IFSC Code</label>
              <input
                type="text"
                placeholder="e.g. HDFC0001234"
                value={bankDetails.ifscCode}
                onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value.toUpperCase() })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 mb-2">Account Number</label>
              <div className="relative">
                <input
                  type={showAccountNumber ? "text" : "password"}
                  placeholder="Enter Account Number"
                  value={bankDetails.accountNumber}
                  onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 pr-10 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                />
                <button
                  type="button"
                  onClick={() => setShowAccountNumber(!showAccountNumber)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors focus:outline-none"
                >
                  {showAccountNumber ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-gray-400 mb-2">Confirm Account Number</label>
              <div className="relative">
                <input
                  type={showConfirmAccountNumber ? "text" : "password"}
                  placeholder="Confirm Account Number"
                  value={bankDetails.confirmAccountNumber}
                  onChange={(e) => setBankDetails({ ...bankDetails, confirmAccountNumber: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 pr-10 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmAccountNumber(!showConfirmAccountNumber)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors focus:outline-none"
                >
                  {showConfirmAccountNumber ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          {withdrawAmount && parseFloat(withdrawAmount) > 0 && (
            <div className="bg-gray-700 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Withdrawal Amount:</span>
                <span className="text-light">₹{parseFloat(withdrawAmount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Processing Fee (2%):</span>
                <span className="text-yellow-400">-₹{withdrawalFee}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-gray-600">
                <span className="text-gray-400 font-medium">Net Amount:</span>
                <span className="text-gold font-bold">₹{netAmount}</span>
              </div>
            </div>
          )}
          <div className="text-sm text-gray-400">
            <p>• Minimum withdrawal: ₹100</p>
            <p>• Processing fee: 2%</p>
            <p>• Processing time: 3-5 business days</p>
            <p>• Please double-check your bank details.</p>
          </div>
          <button
            onClick={handleWithdraw}
            disabled={
              !withdrawAmount ||
              parseFloat(withdrawAmount) <= 0 ||
              parseFloat(withdrawAmount) > balance ||
              !bankDetails.bankName ||
              !bankDetails.ifscCode ||
              !bankDetails.accountNumber ||
              bankDetails.accountNumber !== bankDetails.confirmAccountNumber
            }
            className="w-full bg-gold text-dark py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Request Withdrawal
          </button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Earnings Information</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-400">Total Earnings:</span>
            <span className="text-gold font-medium">₹{balance.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Available for Withdrawal:</span>
            <span className="text-light font-medium">₹{balance.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailerWallet;
